import { DefaultButton, Dropdown, IDropdownOption, MessageBar, MessageBarType, Modal, Spinner, SpinnerSize } from "@fluentui/react";
import { useEffect, useState } from "react";
import { ModalWithCancel } from "../Modals/ModalWithCancel";
import { UseTicketSupportReturn } from "../../hooks/useTicketSupport";
import { TicketHelpType } from "./TicketSupportConstants";
import { SupportTriggerCont, SupportModalOverlay, SupportModalHeader, SupportModalTitle, SupportModalBody, SupportDropdownWrapper, SupportTextareaWrapper, SupportTextareaLabel, SupportTextarea, SupportCharCounter, SupportModalFooter, SupportModalGlobalStyle } from "./TicketSupport.style";

export type TicketSupportProps = Pick<UseTicketSupportReturn, "showSupportForm" | "setShowSupportForm" | "handleSend" | "isSending" | "showResultModal" | "resultTitle" | "resultBody" | "closeResultModal" | "showSuccessToast" | "successToastMessage" | "closeSuccessToast">;

const MAX_LENGTH = 1000;

const helpTypeOptions: IDropdownOption[] =
[
    { key: "", text: "-- Seleccionar tipo (opcional) --" },
    { key: TicketHelpType.Error, text: TicketHelpType.Error },
    { key: TicketHelpType.Help, text: TicketHelpType.Help },
];

const TicketSupport = ({showSupportForm, setShowSupportForm, handleSend, isSending, showResultModal, resultTitle, resultBody, closeResultModal, showSuccessToast, successToastMessage, closeSuccessToast}: TicketSupportProps) =>
{
    const [message, setMessage] = useState("");
    const [helpType, setHelpType] = useState<string>("");

    useEffect(() =>
    {
        if(!showSuccessToast)
        {
            return;
        }

        const timeout = window.setTimeout(() =>
        {
            closeSuccessToast();
        }, 3500);

        return () =>
        {
            window.clearTimeout(timeout);
        };
    }, [showSuccessToast, closeSuccessToast]);

    const dismiss = () =>
    {
        setMessage("");
        setHelpType("");
        setShowSupportForm(false);
    };

    const handleSendClick = async () =>
    {
        if(!message.trim())
        {
            return;
        }
        await handleSend(message, helpType || undefined);
        setMessage("");
        setHelpType("");
    };

    const handleCancelClick = () =>
    {
        dismiss();
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    {
        if(e.target.value.length <= MAX_LENGTH)
        {
            setMessage(e.target.value);
        }
    };

    const isNearLimit = message.length >= MAX_LENGTH * 0.9;

    return(
        <>
            <SupportTriggerCont>
                <DefaultButton className="button tiny-secondary-button support-button" onClick={() => setShowSupportForm(true)}>
                    ¿Necesitas asistencia?
                </DefaultButton>
            </SupportTriggerCont>
            <SupportModalGlobalStyle />
            {showSuccessToast &&
            (
                <MessageBar messageBarType={MessageBarType.success}
                    isMultiline={false} dismissButtonAriaLabel="Cerrar" onDismiss={closeSuccessToast}
                >
                    {successToastMessage}
                </MessageBar>
            )}
            <Modal isOpen={showSupportForm} onDismiss={dismiss} isBlocking={false} className="support-ticket-modal">
                <SupportModalOverlay>
                    <SupportModalHeader>
                        <SupportModalTitle>¿En qué podemos ayudarte?</SupportModalTitle>
                    </SupportModalHeader>
                    <SupportModalBody>
                        <SupportDropdownWrapper>
                            <Dropdown label="Tipo de consulta"
                                options={helpTypeOptions}
                                selectedKey={helpType}
                                onChange={(_, option) => setHelpType(option?.key as string ?? "")}
                                disabled={isSending}
                                placeholder="-- Seleccionar tipo (opcional) --"
                            />
                        </SupportDropdownWrapper>
                        <SupportTextareaWrapper>
                            <SupportTextareaLabel>
                                Describe tu problema <span aria-hidden="true">*</span>
                            </SupportTextareaLabel>
                            <SupportTextarea id="support-message"
                                name="support-message"
                                value={message}
                                onChange={handleMessageChange}
                                placeholder="Explícanos con detalle qué está ocurriendo o en qué necesitas ayuda..."
                                disabled={isSending}
                                rows={6}
                                maxLength={MAX_LENGTH}
                                aria-label="Descripción del problema"
                            />
                            <SupportCharCounter isNearLimit={isNearLimit}>
                                {message.length} / {MAX_LENGTH}
                            </SupportCharCounter>
                        </SupportTextareaWrapper>
                    </SupportModalBody>
                    <SupportModalFooter>
                        <DefaultButton className="button tiny-secondary-button"
                            onClick={handleCancelClick} disabled={isSending}
                        >
                            Cancelar
                        </DefaultButton>
                        <DefaultButton className="button tiny-primary-button"
                            onClick={handleSendClick} disabled={!message.trim() || isSending}
                        >
                            {isSending && <Spinner size={SpinnerSize.small} className="button-spinner" />}
                            {isSending ? "Enviando..." : "Enviar consulta"}
                        </DefaultButton>
                    </SupportModalFooter>
                </SupportModalOverlay>
            </Modal>
            <ModalWithCancel showModal={showResultModal}
                setShowModal={(v) =>
                {
                    if(!v)
                    {
                        closeResultModal();
                    }
                }}
                title={resultTitle}
                body={resultBody}
                onClose={closeResultModal}
                btnSubmitText="Cerrar"
            />
        </>
    );
};

export default TicketSupport;